"use client";

import { DatePicker } from "@/components/ui/date-picker";
import PageHeader from "@/components/layout/PageHeader";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider, DataTableHeader, DataTableHeaderGroup, DataTableHead, DataTableBody, DataTableRow, DataTableCell,
} from "../../components/ui/data-table";
import { get, can } from "../../utils/service";
import { formatDate, formatLabel, categoryLabel, showMoney, showTotal, originalHint, type Paginated } from "@/utils/finance";
import StatusBadge from "@/components/finance/StatusBadge";
import { GridProvider, GridToolbar, StatsStrip, PaginationBar } from "@/components/crm/DataGrid";
import { btnGhost, inputCls } from "@/components/finance/Modal";
import AppSelect from "@/components/ui/app-select";
import SearchInput from "@/components/ui/search-input";

interface Expense {
  id: string; reference: string; title: string; category: string; category_other?: string | null; type: string; status: string; expense_date: string;
  original_amount: string; original_currency: string; base_amount: string; base_currency: string;
  approved_amount: string | null; amount_paid: string; remaining_amount: string | null; rejection_reason: string | null; has_receipt: boolean;
  user?: { id: string; first_name: string; last_name: string };
  display_currency?: string; display_original_amount?: string | null; display_base_amount?: string | null; display_approved_amount?: string | null; display_remaining_amount?: string | null;
}
interface Summary {
  base_currency: string; pending_approval_count: number; pending_approval_amount: string; rejected_count: number;
  awaiting_payment_count: number; total_approved: string; total_reimbursed: string; outstanding: string; draft_count: number;
  display: { currency: string; pending_approval_amount: string; total_approved: string; total_reimbursed: string; outstanding: string } | null;
}
interface Meta { base_currency: string; categories: string[]; statuses: string[]; currencies: string[] }
interface UserOpt { id: string; first_name: string; last_name: string }
interface MemberRow { user: UserOpt }

export default function ExpensesPage() {
  const navigate = useNavigate();
  const admin = can("expenses.view_all"); // reviewers see everyone's expenses
  const canListUsers = can("members.view");
  const canManageCategories = can("expenses.manage_categories");
  const [meta, setMeta] = useState<Meta | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [users, setUsers] = useState<UserOpt[]>([]);
  const [rows, setRows] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ scope: admin ? "all" : "mine", status: "", category: "", currency: "", user_id: "", type: "", date_from: "", date_to: "", search: "" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [pager, setPager] = useState({ total: 0, total_pages: 0 });
  const set = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    get<{ data: Meta }>("/expenses/meta").then((r) => setMeta(r.data)).catch(() => undefined);
    if (canListUsers) get<{ data: { members: MemberRow[] } }>("/members").then((r) => setUsers(r.data.members.map((m) => m.user))).catch(() => undefined);
  }, [admin, canListUsers]);

  useEffect(() => {
    get<{ data: Summary }>("/expenses/summary", { scope: f.scope }).then((r) => setSummary(r.data)).catch(() => undefined);
  }, [f.scope]);

  useEffect(() => { setPage(1); }, [f, perPage]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      const r = await get<Paginated<Expense>>("/expenses", { ...params, page, page_size: perPage });
      setRows(r.data);
      setPager({ total: r.pagination.total, total_pages: r.pagination.total_pages });
    } finally {
      setLoading(false);
    }
  }, [f, page, perPage]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const columns: ColumnDef<Expense>[] = [
    { accessorKey: "reference", header: "Expense", cell: ({ row }) => (<div><p className="text-xs font-semibold text-gray-900 flex items-center gap-1.5">{row.original.title}{row.original.type === "planned_purchase" && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-800">Planned</span>}</p><p className="text-xs text-gray-500">{row.original.reference} · {categoryLabel(row.original)}</p></div>) },
    ...(admin && f.scope === "all" ? [{ id: "user", header: "Submitted by", cell: ({ row }: { row: { original: Expense } }) => <span className="text-xs">{row.original.user ? `${row.original.user.first_name} ${row.original.user.last_name}` : "—"}</span> } as ColumnDef<Expense>] : []),
    { accessorKey: "expense_date", header: "Date", cell: ({ row }) => <span className="text-xs text-gray-600">{formatDate(row.original.expense_date)}</span> },
    { id: "original", header: "Paid amount", cell: ({ row }) => <div><span className="text-xs font-semibold">{showMoney(row.original, "original_amount", "original_currency")}</span>{originalHint(row.original, "original_amount", "original_currency") && <p className="text-[11px] text-gray-400">paid {originalHint(row.original, "original_amount", "original_currency")}</p>}</div> },
    { id: "base", header: "Reimbursable", cell: ({ row }) => (<div><p className="text-xs">{row.original.approved_amount ? showMoney(row.original, "approved_amount", "base_currency") : showMoney(row.original, "base_amount", "base_currency")}</p>{row.original.remaining_amount !== null && parseFloat(row.original.remaining_amount) > 0 && <p className="text-[11px] text-gray-500">{showMoney(row.original, "remaining_amount", "base_currency")} left</p>}</div>) },
    { id: "status", header: "Status", cell: ({ row }) => <StatusBadge kind="expense" status={row.original.status} /> },
  ];

  const bc = summary?.base_currency ?? "UGX";

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full space-y-5">
        <PageHeader title="Expenses" description={admin ? "Review, approve and reimburse expenses paid out of pocket" : "Submit expenses you paid for the company and track reimbursement"}>
          <Link to="/expenses/subscriptions" className={btnGhost}>Subscriptions</Link>
          {canManageCategories && <Link to="/expenses/categories" className={btnGhost}>Categories</Link>}
        </PageHeader>

        <GridProvider id="expenses">
        <div className="space-y-4">
          <GridToolbar
            view={admin ? (
              <div className="inline-flex rounded-full bg-gray-100 p-1 text-xs">
                {[["all", "Everyone"], ["mine", "Mine"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => set("scope", k)} className={`px-4 py-1 rounded-full transition ${f.scope === k ? "bg-white shadow-sm text-gray-900 font-medium" : "text-gray-600"}`}>{l}</button>
                ))}
              </div>
            ) : undefined}
            search={<SearchInput className="w-full sm:max-w-[14rem]" placeholder="Search…" value={f.search} onChange={(e) => set("search", e.target.value)} />}
            filters={<>
              {canListUsers && f.scope === "all" && <label className="text-xs text-gray-600">Submitted by<AppSelect className={`${inputCls} mt-1`} value={f.user_id} onChange={(e) => set("user_id", e.target.value)}><option value="">All users</option>{users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}</AppSelect></label>}
              <label className="text-xs text-gray-600">Status<AppSelect className={`${inputCls} mt-1`} value={f.status} onChange={(e) => set("status", e.target.value)}><option value="">All statuses</option>{meta?.statuses.filter((s) => admin && f.scope === "all" ? s !== "draft" : true).map((s) => <option key={s} value={s}>{s === "approved" ? "Approved · pending payment" : formatLabel(s)}</option>)}</AppSelect></label>
              <label className="text-xs text-gray-600">Category<AppSelect className={`${inputCls} mt-1`} value={f.category} onChange={(e) => set("category", e.target.value)}><option value="">All categories</option>{meta?.categories.map((c) => <option key={c} value={c}>{formatLabel(c)}</option>)}</AppSelect></label>
              <label className="text-xs text-gray-600">Type<AppSelect className={`${inputCls} mt-1`} value={f.type} onChange={(e) => set("type", e.target.value)}><option value="">All types</option><option value="reimbursement">Reimbursements</option><option value="planned_purchase">Planned purchases</option></AppSelect></label>
              <label className="text-xs text-gray-600">Currency<AppSelect className={`${inputCls} mt-1`} value={f.currency} onChange={(e) => set("currency", e.target.value)}><option value="">Any currency</option>{meta?.currencies.map((c) => <option key={c} value={c}>{c}</option>)}</AppSelect></label>
              <div className="grid grid-cols-2 gap-2">
                <DatePicker value={f.date_from} onChange={(v) => set("date_from", v)} placeholder="From date" max={f.date_to || undefined} clearable aria-label="From date" />
                <DatePicker value={f.date_to} onChange={(v) => set("date_to", v)} placeholder="To date" min={f.date_from || undefined} clearable aria-label="To date" />
              </div>
            </>}
            activeFilters={["user_id", "status", "category", "type", "currency", "date_from", "date_to"].filter((k) => f[k as keyof typeof f]).length}
            onClearFilters={() => setF((cur) => ({ ...cur, user_id: "", status: "", category: "", type: "", currency: "", date_from: "", date_to: "" }))}
            hasStats={!!summary}
            exportEntity="expenses"
            exportPermission="expenses.export"
            onAdd={() => navigate("/expenses/new")}
            addLabel="New Expense"
          />

          {summary && (
            <StatsStrip items={[
              { label: "Awaiting approval", value: String(summary.pending_approval_count), hint: showTotal(summary.display, "pending_approval_amount", { pending_approval_amount: summary.pending_approval_amount, currency: bc }), tone: summary.pending_approval_count ? "warn" : "default" },
              { label: "Awaiting payment", value: String(summary.awaiting_payment_count), hint: `${showTotal(summary.display, "outstanding", { outstanding: summary.outstanding, currency: bc })} outstanding` },
              { label: "Reimbursed", value: showTotal(summary.display, "total_reimbursed", { total_reimbursed: summary.total_reimbursed, currency: bc }), hint: `of ${showTotal(summary.display, "total_approved", { total_approved: summary.total_approved, currency: bc })} approved`, tone: "good" },
              { label: "Rejected", value: String(summary.rejected_count), hint: summary.draft_count ? `${summary.draft_count} draft${summary.draft_count === 1 ? "" : "s"}` : undefined, tone: summary.rejected_count ? "bad" : "default" },
            ]} />
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            {loading ? (
              <div className="p-6 animate-pulse space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-10 bg-gray-100 rounded" />)}</div>
            ) : rows.length ? (
              <TableProvider columns={columns} data={rows}>
                <DataTableHeader>{({ headerGroup }) => <DataTableHeaderGroup headerGroup={headerGroup}>{({ header }) => <DataTableHead header={header} />}</DataTableHeaderGroup>}</DataTableHeader>
                <DataTableBody>{({ row }) => (
                  <DataTableRow row={row} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/expenses/${(row.original as Expense).id}`)}>
                    {({ cell }) => <DataTableCell cell={cell} />}
                  </DataTableRow>
                )}</DataTableBody>
              </TableProvider>
            ) : (
              <p className="p-10 text-center text-sm text-gray-500">No expenses match these filters.</p>
            )}
            <PaginationBar page={page} totalPages={pager.total_pages} total={pager.total} perPage={perPage} onPage={setPage} onPerPage={setPerPage} noun="expenses" />
          </div>
        </div>
        </GridProvider>
      </div>
    </div>
  );
}
